import axios from 'axios';
import { BASE_API, USER_TOKEN } from 'config/consts';

const URL_SETTINGS = `${BASE_API}/manager/guests/settings`;
const URL_LIST = `${BASE_API}/manager/guests`;

const authHeaders = () => ({
  headers: {
    Authorization: `${JSON.parse(localStorage.getItem(USER_TOKEN))}`,
  },
});

const snackbar = (message, variant = 'success') => ({
  type: 'SNACKBAR',
  payload: { message, variant, open: true },
});

const FORBIDDEN_CODES = new Set([
  'forbidden_not_manager',
  'forbidden_not_sindilegis_manager', // compat: nome antigo do gate
  'forbidden_station', // compat: nome ainda mais antigo
  'guest_feature_unavailable',
]);

const withStation = (stationId, params = {}) =>
  stationId ? { ...params, station_id: stationId } : params;

const handleForbidden = (e, onForbidden) => {
  if (
    e?.response?.status === 403 &&
    FORBIDDEN_CODES.has(e?.response?.data?.code) &&
    typeof onForbidden === 'function'
  ) {
    onForbidden();
    return true;
  }
  return false;
};

export const getSettings =
  (stationId, { onForbidden } = {}) =>
  (dispatch) => {
    dispatch({ type: 'GUEST_CONTROL_SETTINGS_LOAD', payload: true });
    axios
      .get(URL_SETTINGS, { ...authHeaders(), params: withStation(stationId) })
      .then((response) => {
        dispatch({ type: 'GUEST_CONTROL_SETTINGS_FETCHED', payload: response.data });
      })
      .catch((e) => {
        dispatch({ type: 'GUEST_CONTROL_SETTINGS_LOAD', payload: false });
        if (handleForbidden(e, onForbidden)) return;
        dispatch(
          snackbar(
            e?.response?.data?.error || 'Erro ao carregar configurações de convidados.',
            'error'
          )
        );
      });
  };

export const updateSettings = (stationId, payload) => (dispatch) => {
  dispatch({ type: 'GUEST_CONTROL_SETTINGS_SAVE', payload: true });
  axios
    .put(URL_SETTINGS, withStation(stationId, payload), authHeaders())
    .then((response) => {
      dispatch({ type: 'GUEST_CONTROL_SETTINGS_FETCHED', payload: response.data });
      dispatch({
        type: 'STATION_FLAGS_UPDATED',
        payload: {
          stationId: response.data.station_id,
          flags: {
            guest_feature_enabled: !!response.data.guest_feature_enabled,
            friend_invite_limit: response.data.friend_invite_limit || 0,
            limit_by_audience_enabled: !!response.data.limit_by_audience_enabled,
          },
        },
      });
      dispatch({ type: 'GUEST_CONTROL_SETTINGS_SAVE', payload: false });
      dispatch(snackbar('Configurações atualizadas.'));
    })
    .catch((e) => {
      dispatch({ type: 'GUEST_CONTROL_SETTINGS_SAVE', payload: false });
      dispatch(snackbar(e?.response?.data?.error || 'Erro ao atualizar configurações.', 'error'));
    });
};

const FILTER_KEYS = [
  'page',
  'limit',
  'status',
  'guest_type',
  'invited_name',
  'invited_cpf',
  'invited_email',
  'inviter_name',
  'invited_from',
  'invited_to',
];

const buildQs = (filters, stationId) => {
  const params = new URLSearchParams();
  FILTER_KEYS.forEach((k) => {
    const v = filters[k];
    if (v !== undefined && v !== null && v !== '') {
      params.set(k, v);
    }
  });
  if (stationId) params.set('station_id', stationId);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

export const getGuests =
  (stationId, filters = {}, { onForbidden } = {}) =>
  (dispatch) => {
    dispatch({ type: 'GUEST_CONTROL_LIST_LOAD', payload: true });
    axios
      .get(`${URL_LIST}${buildQs(filters, stationId)}`, authHeaders())
      .then((response) => {
        dispatch({ type: 'GUEST_CONTROL_LIST_FETCHED', payload: response.data });
      })
      .catch((e) => {
        dispatch({ type: 'GUEST_CONTROL_LIST_LOAD', payload: false });
        if (handleForbidden(e, onForbidden)) return;
        dispatch(
          snackbar(e?.response?.data?.error || 'Erro ao carregar lista de convidados.', 'error')
        );
      });
  };

// Busca a lista completa filtrada (sem paginação) para exportação.
// Não escreve no Redux store — retorna a Promise pra quem chamou.
export const fetchGuestsForExport =
  (stationId, filters = {}) =>
  () =>
    axios
      .get(`${URL_LIST}${buildQs({ ...filters, limit: 'all' }, stationId)}`, authHeaders())
      .then((r) => r.data);
